// ゲーム（iframe の中）とシェル（親のページ）が postMessage でやりとりする約束事。
// ゲームはこの口しか知らない。広告・保存・計測・ログインの実体はシェルの裏で差し替える。

export const PROTOCOL = "appful/1" as const;
export const SDK_VERSION = "1.0.0";

export type Locale = "ja" | "en";
export const LOCALES: readonly Locale[] = ["ja", "en"];

/** セーブに入れられる値。JSON にできる物だけ。 */
export type Json = null | boolean | number | string | Json[] | { [key: string]: Json };

export type Period = "all" | "daily";

export interface UserInfo {
  signedIn: boolean;
  /** ランキングに出る名前。ゲストは null。本名ではなく、利用者が決めた表示名。 */
  displayName: string | null;
}

export interface InitResult {
  gameId: string;
  locale: Locale;
  user: UserInfo;
  /** 利用者が音を消しているか。true のあいだは音を鳴らさない */
  muted: boolean;
  sdkVersion: string;
}

export interface LeaderboardEntry {
  rank: number;
  displayName: string;
  score: number;
  isMe: boolean;
}

/** 終わった後の画面（一覧の側が出す）に渡す物 */
export interface ResultParams {
  /** 今回の点（整数） */
  score: number;
  /** ランキングに出すときの board の id。無ければランキングには出さない */
  boardId?: string;
  /** 結果に合わせた短い一言（今の言語で。60字まで） */
  message?: string;
}

export type SubmitResult =
  | { status: "submitted"; best: number; improved: boolean; rank: number | null }
  | { status: "signin_required" }
  | { status: "rejected"; reason: string }
  | { status: "offline" };

/** オンライン対戦の部屋にいる1人。名前は自動の呼び名（本名・ID は渡さない） */
export interface OnlinePlayer {
  /** 部屋の中の席の番号（0 から） */
  seat: number;
  /** チームの番号。チームが無い遊び（1対1・全員が敵）は席の番号と同じ */
  team: number;
  displayName: string;
  /** いま部屋につながっているか */
  connected: boolean;
}

export interface OnlineRoom {
  /** 自分の席 */
  me: number;
  /** 遊びの進行を決める役の席。その人が抜けたら、残った人の中で小さい席に移る */
  host: number;
  mode: string;
  players: OnlinePlayer[];
}

export type JoinResult =
  | { status: "matched"; room: OnlineRoom }
  | { status: "signin_required" }
  /** leave() で探すのをやめた */
  | { status: "cancelled" }
  /** 対戦を止めている・つながらない */
  | { status: "unavailable" };

/**
 * AI に頼む物（appful.ai.generate。docs/AI.md）。AI を呼ぶのは基盤の関数で、ゲームには鍵も身分も渡さない。
 * game.json に ai: true を書いて出したゲームは、そのまま使える（1 本 1 日の上限まで・ログインした人だけ。基盤の担当が止められる）。
 */
export interface AiGenerateParams {
  /** 頼む文（4000 字まで） */
  prompt: string;
  /** AI の役や答え方の決まり（4000 字まで） */
  system?: string;
  /** 答えの JSON の形（JSON Schema の形。JSON の文字で 4000 字まで）。渡すと、答えを JSON で受け取り、読んだ値が json に入る */
  schema?: { [key: string]: Json };
  /** 答えの長さの上限（トークン）。既定 512・上限 2048（超えた分は 2048 に丸める） */
  maxTokens?: number;
}

export interface AiGenerateResult {
  /** 答えの文（schema を渡したときは JSON の文字） */
  text: string;
  /** schema を渡したときだけ：text を JSON として読んだ値 */
  json?: Json;
}

/**
 * appful.ai.generate が失敗したときの Error の code。どれでもゲームは止まらずに遊べるようにする（docs/GAME-REQUIREMENTS.md の「AI を使う」）。
 * not-enabled：このゲームは AI を使えない（基盤が許していない・全体を止めている・ログインが要るのにゲストのまま）
 * daily-limit：今日の上限に届いた（明日の 0 時（日本時間）に戻る）／rate-limit：短い間に呼びすぎた（少し待つ）
 * too-long：頼む文が長すぎる／blocked：安全のために AI が答えを止めた／unavailable：そのほか（時間切れ・通信・使い方の誤り。message を見る）
 */
export const AI_ERROR_CODES = ["not-enabled", "daily-limit", "rate-limit", "too-long", "blocked", "unavailable"] as const;
export type AiErrorCode = (typeof AI_ERROR_CODES)[number];

/** 決まったスタンプ（自由な文字は送れない）。表示はシェルが受け持ち、ゲームにも知らせる */
export const STAMPS = ["hello", "nice", "thanks", "sorry", "gg", "again", "wow", "go"] as const;
export type StampId = (typeof STAMPS)[number];

/** 呼べる口の一覧。params はゲームから送る物、result はシェルから返る物。 */
export interface Methods {
  init: { params: { sdkVersion: string }; result: InitResult };
  "lifecycle.loadingFinished": { params: Record<string, never>; result: null };
  "lifecycle.gameplayStart": { params: Record<string, never>; result: null };
  "lifecycle.gameplayStop": { params: Record<string, never>; result: null };
  "ads.interstitial": { params: Record<string, never>; result: { shown: boolean } };
  "ads.rewarded": { params: Record<string, never>; result: { granted: boolean } };
  "save.get": { params: { key: string }; result: { value: Json | null } };
  "save.set": { params: { key: string; value: Json }; result: { cloud: boolean } };
  "save.remove": { params: { key: string }; result: { cloud: boolean } };
  "leaderboard.submit": { params: { boardId: string; score: number }; result: SubmitResult };
  "leaderboard.top": {
    params: { boardId: string; period?: Period; limit?: number };
    result: { entries: LeaderboardEntry[] };
  };
  /** 結果の画面を出す。利用者が「もう一回」を押したら返る（ほかのゲームへ行ったときは返らない） */
  "result.show": { params: ResultParams; result: { action: "retry" } };
  "user.get": { params: Record<string, never>; result: UserInfo };
  "user.requestSignIn": { params: Record<string, never>; result: UserInfo };
  "events.track": {
    params: { name: string; params?: Record<string, string | number | boolean> };
    result: null;
  };
  /** 相手を探して部屋に入る。見つかるか、やめるまで返らない */
  "online.join": { params: { mode: string }; result: JoinResult };
  "online.leave": { params: Record<string, never>; result: null };
  /** 部屋のほかの人へ送る。to を付けるとその席だけへ */
  "online.send": { params: { data: Json; to?: number }; result: null };
  /**
   * 順位を出す（席ごとの順位。1 が1位。同じ順位があってよい）。
   * つながっている全員の出した物が揃えば agreed: true。食い違えば多い方。決められなければ false。
   */
  "online.result": { params: { placements: number[] }; result: { agreed: boolean; placements: number[] | null } };
  /** AI に頼む。ゲームの id はシェルが開いているゲームの物を使う（ゲームからは渡さない） */
  "ai.generate": { params: AiGenerateParams; result: AiGenerateResult };
}

export type MethodName = keyof Methods;
export const METHOD_NAMES: readonly MethodName[] = [
  "init",
  "lifecycle.loadingFinished",
  "lifecycle.gameplayStart",
  "lifecycle.gameplayStop",
  "ads.interstitial",
  "ads.rewarded",
  "save.get",
  "save.set",
  "save.remove",
  "leaderboard.submit",
  "leaderboard.top",
  "result.show",
  "user.get",
  "user.requestSignIn",
  "events.track",
  "online.join",
  "online.leave",
  "online.send",
  "online.result",
  "ai.generate",
];

/** シェルからゲームへ一方的に知らせる出来事。 */
export interface Events {
  /** ログインの状態が変わった */
  user: UserInfo;
  /** 表示の言語が変わった */
  locale: { locale: Locale };
  /** 音の入切が変わった */
  sound: { muted: boolean };
  /** 広告の間など。音と入力を止める */
  pause: Record<string, never>;
  resume: Record<string, never>;
  /** 部屋のほかの人から届いた物 */
  "online.message": { from: number; data: Json };
  /** だれかが抜けた・戻った */
  "online.player": { seat: number; connected: boolean };
  /** 進行を決める役が移った */
  "online.host": { seat: number };
  /** だれかがスタンプを送った（シェルも画面に出す） */
  "online.stamp": { from: number; id: StampId };
  /** 部屋が閉じた */
  "online.closed": { reason: "left" | "timeout" | "server" | "alone" };
}
export type EventName = keyof Events;

export type ErrorCode =
  | "bad_request"
  | "unknown_method"
  | "rate_limited"
  | "too_large"
  | "not_ready"
  | "internal"
  // ai.generate の失敗（AI_ERROR_CODES）。SDK は、ほかの code も AI の code に直してから返す
  | AiErrorCode;

export interface RequestMessage<M extends MethodName = MethodName> {
  protocol: typeof PROTOCOL;
  kind: "request";
  id: number;
  method: M;
  params: Methods[M]["params"];
}

export type ResponseMessage<M extends MethodName = MethodName> =
  | { protocol: typeof PROTOCOL; kind: "response"; id: number; ok: true; result: Methods[M]["result"] }
  | { protocol: typeof PROTOCOL; kind: "response"; id: number; ok: false; error: { code: ErrorCode; message: string } };

export interface EventMessage<E extends EventName = EventName> {
  protocol: typeof PROTOCOL;
  kind: "event";
  event: E;
  data: Events[E];
}

// 上限。シェルとゲームの両方がこの値で確かめる。
export const LIMITS = {
  /** 1本のゲームのセーブの合計（JSON の文字数）。短いゲーム */
  saveBytes: 64 * 1024,
  /**
   * 長いゲーム（game.json の length: "long"）のセーブの合計。Firestore の1文書は 1MB までで、
   * 日本語は1文字 3 バイトになりうるので、256K 文字（最大 768KB）にする
   */
  saveBytesLong: 256 * 1024,
  keyPattern: /^[A-Za-z0-9_.-]{1,64}$/,
  boardIdPattern: /^[a-z0-9][a-z0-9_-]{0,31}$/,
  eventNamePattern: /^[a-z][a-z0-9_]{0,39}$/,
  eventParams: 10,
  eventStringLength: 100,
  leaderboardLimit: 50,
  resultMessageLength: 60,
  /** 対戦で1回に送れる量（JSON の文字数） */
  onlineMessageBytes: 2048,
  /** 1部屋の人数の上限 */
  onlinePlayers: 16,
  modeIdPattern: /^[a-z0-9][a-z0-9_-]{0,31}$/,
  /** AI に頼む文・役の決まり・答えの形（JSON の文字）の長さの上限。関数（functions/src/ai-core.ts の AI_LIMITS）と同じ値 */
  aiPromptChars: 4000,
  aiSystemChars: 4000,
  aiSchemaChars: 4000,
  /** 答えの長さ（トークン）の既定と上限 */
  aiDefaultMaxTokens: 512,
  aiMaxTokens: 2048,
} as const;

export function isProtocolMessage(data: unknown): data is { protocol: string; kind: string } {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { protocol?: unknown }).protocol === PROTOCOL &&
    typeof (data as { kind?: unknown }).kind === "string"
  );
}

/** 日ごとのランキングの区切り。日本時間の 0 時で切り替える。 */
export function periodKey(period: Period, now: Date = new Date()): string {
  if (period === "all") return "all";
  const jst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  const y = jst.getUTCFullYear();
  const m = String(jst.getUTCMonth() + 1).padStart(2, "0");
  const d = String(jst.getUTCDate()).padStart(2, "0");
  return `d${y}${m}${d}`;
}

/** Firestore の leaderboards の文書の ID。 */
export function boardDocId(gameId: string, boardId: string, key: string): string {
  return `${gameId}__${boardId}__${key}`;
}
